import React from 'react';

import styled from '@emotion/styled';
import { useTranslations } from 'next-intl';

import { Button } from '@web/ui/components/inputs/Button';
import { WiFiAntenna } from '@web/ui/icons';
import { getStateLayerColor, getTextShadow } from '@web/ui/theme/utils';

import { StakingConnectButton } from './StakingConnectButton';
import { StakingTerminalContainer } from './StakingTerminal';
import { BackgroundStroke } from './StakingTerminal/common';
import BoostAnimation from './StakingTerminal/StakingBoost/boost.gif?url';
import { StakingConsole } from './StakingTerminal/StakingConsole';

const BoostContainer = styled.div`
  display: flex;
  position: relative;
  justify-content: center;
  align-items: center;
  border-radius: ${({ theme }) => theme.shape.corner['1250']};
  margin: ${({ theme }) => theme.spacing('1000')};
  background-image: ${getStateLayerColor('greenFixed', 'semiTransparent')},
    url('${BoostAnimation}');
  background-size: auto, contain;
  background-position: center;
  background-repeat: no-repeat;

  min-height: 24rem;
`;

const TerminalTiles = styled.div`
  order: -1;
  display: grid;
  gap: ${({ theme }) => theme.spacing('1000')};
  padding: ${({ theme }) => theme.spacing('1000')};

  ${({ theme }) => theme.breakpoints.up('md')} {
    order: unset;
  }
`;

const ConnectContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing('1000')};
  max-width: 22.5rem;
`;

const WelcomeText = styled.p`
  align-self: stretch;
  color: ${({ theme }) => theme.color.greenFixed};
  font: ${({ theme }) => theme.typography.brand.bold};
  font-size: ${({ theme }) => theme.typescale.size[10]};
  line-height: ${({ theme }) => theme.typescale.lineHeight.default};
  letter-spacing: ${({ theme }) => theme.typescale.tracking.decreased};
  text-align: center;
  text-shadow: ${({ theme }) => getTextShadow(theme.chipPoints.bonus.shadow)};
`;

const ConnectedTo = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing('0500')};
  position: absolute;
  top: ${({ theme }) => theme.spacing('1000')};
  left: ${({ theme }) => theme.spacing('1000')};
`;

const NoSignal = styled.p`
  color: ${({ theme }) => theme.color.greenFixed};
  font: ${({ theme }) => theme.typography.body.bold};
  font-size: ${({ theme }) => theme.typescale.size['0750']};
  text-shadow: ${({ theme }) => getTextShadow(theme.chipPoints.bonus.shadow)};
`;

const WiFiAntennaIcon = styled(WiFiAntenna)`
  width: ${({ theme }) => theme.size[1250]};
  height: ${({ theme }) => theme.size[1250]};
  flex-shrink: 0;
  filter: drop-shadow(
    ${({ theme }) => getTextShadow(theme.chipPoints.bonus.shadow)}
  );
`;

const StyledButton = styled(Button)`
  & div {
    padding-inline: 1rem;
  }
`;

export const ConnectConsole = () => {
  const t = useTranslations();

  return (
    <StakingTerminalContainer>
      <BackgroundStroke />
      <StakingConsole
        blockchain={`[${t('CONNECT_CONSOLE_NOT_CONNECTED')}]`}
        numberOfStaked={t('NOT_AVAILABLE')}
        numberOfUnstaked={t('NOT_AVAILABLE')}
      />
      <BoostContainer>
        <TerminalTiles>
          <ConnectedTo>
            <WiFiAntennaIcon size="1250" />
            <NoSignal>{t('CONNECT_CONSOLE_NO_SIGNAL')}</NoSignal>
          </ConnectedTo>
          <ConnectContainer>
            <WelcomeText>{t('CONNECT_CONSOLE_WELCOME_MESSAGE')}</WelcomeText>
            <StakingConnectButton
              render={({ onClick, isLoading }) => (
                <StyledButton
                  onClick={onClick}
                  variant="secondary"
                  color="onGreenFixed"
                  background="greenFixed"
                  disabled={isLoading}
                  label={
                    isLoading
                      ? t('STAKING_TERMINAL_LOADING_BUTTON')
                      : t('STAKING_TERMINAL_CONNECT_BUTTON')
                  }
                />
              )}
            />
          </ConnectContainer>
        </TerminalTiles>
      </BoostContainer>
    </StakingTerminalContainer>
  );
};
